/**
 * Role-Based Access Control Configuration
 * Maps roles to allowed routes/views in the POS application
 */

// Define route groups for easier management
const ROUTE_GROUPS = {
  DASHBOARD: ['/dashboard'],
  PRODUCTS: [
    '/products',
    '/products/new',
    '/products/update-price',
    '/products/variations',
    '/products/bulk-import',
    '/products/bulk-stock-import',
    '/products/price-groups',
    '/products/units',
    '/products/categories',
    '/products/brands',
    '/products/warranties',
    '/products/recipes',
    '/inventory/stock-adjustments',
  ],
  SALES: [
    '/sales',
    '/sales/pos',
    '/sales/orders',
    '/sales/history',
    '/sales/invoice/new',
    '/sales/pos-opening-entries',
    '/sales/invoice/:id',
    '/sales/pos-opening-entries/:name',
    '/sales/pos-invoice/:id',
    '/sales/invoice/:id/edit',
  ],
  WAREHOUSES: [
    '/warehouses',
    '/warehouses/new',
    '/warehouses/:id',
    '/warehouses/:id/edit',
    '/warehouses/:id/staff',
  ],
  PRODUCTION: [
    '/production',
    '/production/kitchen-foods',
    '/production/factors',
    '/production/monitor',
    '/production/uom',
  ],
  INVENTORY: [
    '/inventory',
    '/inventory/stock-summary',
    '/inventory/low-stock',
    '/inventory/stock-ledger',
    '/inventory/item-details',
    '/inventory/stock-entries',
    '/inventory/stock-entries/:stockEntryName',
    '/inventory/material-receipts',
    '/inventory/material-issues',
    '/inventory/material-transfers',
    '/inventory/stock-entry',
    '/inventory/material-receipt',
    '/inventory/material-issue',
    '/inventory/material-transfer',
    '/inventory/counter-check',
    '/inventory/stock-snapshot',
    '/inventory/stock-reconciliation',
    '/inventory/multi-level-reconciliation',
    '/inventory/multi-level-reconciliation/new',
    '/inventory/multi-level-reconciliation/:id',
    '/inventory/multi-level-reconciliation/:id/stock-take',
    '/reports/inventory/stock-summary',
    '/reports/inventory/low-stock',
    '/reports/inventory/item-details',
    '/reports/inventory/stock-entries',
    '/reports/inventory/stock-entries/:stockEntryName',
    '/reports/inventory/material-receipts',
    '/reports/inventory/material-issues',
    '/reports/inventory/material-transfers',
    '/reports/inventory/stock-entry',
    '/reports/inventory/material-receipt',
    '/reports/inventory/material-issue',
    '/reports/inventory/material-transfer',
    '/reports/inventory/stock-reconciliation',
    '/reports/inventory/multi-level-reconciliation',
    '/reports/inventory/multi-level-reconciliation/new',
    '/reports/inventory/multi-level-reconciliation/:id',
    '/reports/inventory/multi-level-reconciliation/:id/stock-take',
  ],
  CUSTOMERS: [
    '/customers',
    '/customers/credit',
    '/customers/report',
    // CRM module (Customer 360 analytics, promotions, broadcasts)
    '/crm',
    '/crm/promotions',
    '/crm/broadcasts',
    '/crm/customers/:id',
  ],
  SUPPLIERS: [
    '/suppliers',
    '/suppliers/groups',
    '/suppliers/:id',
    '/suppliers/report',
  ],
  PURCHASES: [
    '/purchases',
    '/purchases/new',
    '/purchases/create-order',
    '/purchases/submit-order',
    '/purchases/grns',
    '/purchases/grns/:id',
    '/purchases/returns',
    '/purchases/receipts',
    '/purchases/receipts/new',
    '/purchases/:id',
    '/purchases/receipts/:id',
    '/purchases/invoices',
    '/purchases/invoices/:invoiceNo',
    '/purchases/returns/:id',
    '/purchases/expenses',
  ],
  STOCK_TRANSFERS: [
    '/stock-transfers',
    '/stock-transfers/list',
    '/stock-transfers/create-request',
    '/stock-transfers/:id',
    '/stock-transfers/:id/approve',
    '/stock-transfers/:id/dispatch',
    '/stock-transfers/:id/receive',
  ],
  STAFF: ['/staff'],
  SHIFT_MANAGEMENT: ['/shift-management'],
  ROLES: [
    '/roles',
    '/roles/new',
    '/roles/:roleName',
    '/roles/:roleName/edit',
    '/roles/:roleName/permissions',
  ],
  REPORTS: [
    '/reports',
    '/reports/accounts',
    '/reports/accounts/z-report',
    '/reports/accounts/profit-and-loss',
    '/reports/sales-analytics',
    '/reports/sales-analytics/report',
    '/reports/sales-analytics/table',
    '/reports/sales-analytics/product-analytics',
    '/reports/sales-performance',
    '/reports/waiter-revenue',
    '/reports/inventory-summary',
    '/reports/inventory-valuation',
    '/reports/inventory-valuation/value-by-category',
    '/reports/inventory-valuation/aggregated-value',
    '/reports/inventory-valuation/cost-method-comparison',
    '/reports/inventory-valuation/value-trends',
    '/reports/aging-stock',
    '/reports/aging-stock/stock-aging',
    '/reports/aging-stock/obsolescence-risk',
    '/reports/aging-stock/aging-recommendations',
    '/reports/performance-metrics',
    '/reports/performance-metrics/accuracy',
    '/reports/performance-metrics/variance',
    '/reports/performance-metrics/adjustment-trends',
    '/reports/performance-metrics/transfer-efficiency',
    '/staff-performance',
  ],
  SETTINGS: [
    '/settings',
    '/settings/business',
    '/settings/pos-profile',
    '/settings/etims',
    '/settings/bank-accounts',
    '/settings/account-provisioning',
    '/settings/payment-methods',
    '/settings/inventory-discounts',
    '/settings/inventory-discounts/new',
    '/settings/inventory-discounts/:id/edit',
    '/settings/loyalty-programs',
    '/settings/module-visibility',
  ],
  INDUSTRY: [
    '/industry/:industryCode/products',
  ],
  KDS: ['/kds'],
  BARBERSHOP: [
    '/attendance',
    '/staff-performance',
    '/marketing',
    '/crm/promotions',
    '/crm/broadcasts',
  ],
};

// Helper function to combine route groups
const combineRoutes = (...groups) => {
  return groups.flatMap(group => ROUTE_GROUPS[group] || []);
};

/**
 * Role-based access configuration
 * Each role maps to an array of allowed route paths
 * Routes can be specified individually or by using route groups
 */
// Full-access route list reused by Administrator-tier roles
const _ALL_ROUTES = () => combineRoutes(
  'DASHBOARD',
  'PRODUCTS',
  'SALES',
  'WAREHOUSES',
  'INVENTORY',
  'CUSTOMERS',
  'SUPPLIERS',
  'PURCHASES',
  'STOCK_TRANSFERS',
  'STAFF',
  'ROLES',
  'REPORTS',
  'SETTINGS',
  'INDUSTRY',
  'KDS',
  'SHIFT_MANAGEMENT',
  'BARBERSHOP'
);

export const ROLE_ACCESS_CONFIG = {
  // ─── Default POS roles (seeded by provision_default_roles.py) ─────────────

  // POS Administrator – full visibility, mirrors Administrator
  'POS Administrator': [..._ALL_ROUTES()],

  // POS Manager – full operations; no role management, audit logs, or system settings
  'POS Manager': [
    ...combineRoutes(
      'DASHBOARD',
      'PRODUCTS',
      'SALES',
      'WAREHOUSES',
      'INVENTORY',
      'CUSTOMERS',
      'SUPPLIERS',
      'PURCHASES',
      'STOCK_TRANSFERS',
      'STAFF',
      'REPORTS',
    ),
    // Scoped settings only
    '/settings',
    '/settings/pos-profile',
    '/settings/payment-methods',
    '/settings/loyalty-programs',
    '/settings/inventory-discounts',
    '/settings/inventory-discounts/new',
    '/settings/inventory-discounts/:id/edit',
    '/settings/module-visibility',
  ],

  // Cashier – POS sales operations + customer lookup + product browse
  Cashier: [
    '/dashboard',
    ...combineRoutes('SALES', 'CUSTOMERS'),
    '/products',
    '/inventory/stock-summary',
    '/inventory/low-stock',
  ],

  // Inventory Manager – products, stock, purchases, warehouses, reports
  'Inventory Manager': [
    ...combineRoutes(
      'DASHBOARD',
      'PRODUCTS',
      'INVENTORY',
      'WAREHOUSES',
      'PURCHASES',
      'SUPPLIERS',
      'STOCK_TRANSFERS',
    ),
    // Inventory reports only
    '/reports',
    '/reports/inventory-summary',
    '/reports/inventory-valuation',
    '/reports/inventory-valuation/value-by-category',
    '/reports/inventory-valuation/aggregated-value',
    '/reports/inventory-valuation/cost-method-comparison',
    '/reports/inventory-valuation/value-trends',
    '/reports/aging-stock',
    '/reports/aging-stock/stock-aging',
    '/reports/aging-stock/obsolescence-risk',
    '/reports/aging-stock/aging-recommendations',
    '/reports/performance-metrics',
    '/reports/performance-metrics/accuracy',
    '/reports/performance-metrics/variance',
    '/reports/performance-metrics/adjustment-trends',
    '/reports/performance-metrics/transfer-efficiency',
    '/reports/inventory/stock-summary',
    '/reports/inventory/low-stock',
    '/reports/inventory/item-details',
    '/reports/inventory/stock-entries',
    '/reports/inventory/stock-entries/:stockEntryName',
    '/reports/inventory/material-receipts',
    '/reports/inventory/material-issues',
    '/reports/inventory/material-transfers',
    '/reports/inventory/stock-reconciliation',
    // Read-only sales history for stock cross-reference
    '/sales/history',
  ],

  // Accountant – financial visibility, reports, accounts settings
  Accountant: [
    '/dashboard',
    '/sales/history',
    '/purchases',
    '/purchases/receipts',
    '/purchases/receipts/:id',
    '/purchases/invoices',
    '/purchases/invoices/:invoiceNo',
    ...combineRoutes('CUSTOMERS', 'SUPPLIERS', 'REPORTS'),
    '/settings',
    '/settings/bank-accounts',
    '/settings/account-provisioning',
    '/settings/payment-methods',
    '/settings/loyalty-programs',
  ],

  // Administrator - Full access to everything
  Administrator: [
    ...combineRoutes(
      'DASHBOARD',
      'PRODUCTS',
      'SALES',
      'WAREHOUSES',
      'INVENTORY',
      'CUSTOMERS',
      'SUPPLIERS',
      'PURCHASES',
      'STOCK_TRANSFERS',
      'STAFF',
      'ROLES',
      'REPORTS',
      'SETTINGS',
      'INDUSTRY',
      'KDS',
      'BARBERSHOP'
    ),
  ],

  // System Manager - Full access similar to Administrator
  'System Manager': [
    ...combineRoutes(
      'DASHBOARD',
      'PRODUCTS',
      'SALES',
      'WAREHOUSES',
      'INVENTORY',
      'CUSTOMERS',
      'SUPPLIERS',
      'PURCHASES',
      'STOCK_TRANSFERS',
      'STAFF',
      'ROLES',
      'REPORTS',
      'SETTINGS',
      'INDUSTRY',
      'KDS',
      'BARBERSHOP'
    ),
  ],

  // All - Full access (catch-all role)
  All: [
    ...combineRoutes(
      'DASHBOARD',
      'PRODUCTS',
      'SALES',
      'WAREHOUSES',
      'INVENTORY',
      'CUSTOMERS',
      'SUPPLIERS',
      'PURCHASES',
      'STOCK_TRANSFERS',
      'STAFF',
      'ROLES',
      'REPORTS',
      'SETTINGS',
      'INDUSTRY',
      'KDS',
      'BARBERSHOP'
    ),
  ],

  // Sales roles
  'Sales Manager': [
    ...combineRoutes('DASHBOARD', 'SALES', 'CUSTOMERS', 'PRODUCTS', 'REPORTS'),
    '/inventory/stock-summary', // View stock for sales
  ],

  'Sales User': [
    '/dashboard',
    '/sales',
    '/sales/history',
    '/customers',
    '/products', // View products for sales
    '/inventory', // Access to inventory section
    '/inventory/stock-summary', // Check stock availability
    // Stock reconciliation access
    '/inventory/stock-reconciliation',
    // Multi-level reconciliation access (can add stock take when status is "Pending Sales User")
    '/inventory/multi-level-reconciliation',
    '/inventory/multi-level-reconciliation/:id',
    '/inventory/multi-level-reconciliation/:id/stock-take',
  ],

  // Purchase roles
  'Purchase Manager': [
    ...combineRoutes('DASHBOARD', 'PURCHASES', 'SUPPLIERS', 'INVENTORY', 'REPORTS'),
    '/products', // View products
    '/purchases/create-order',
    '/purchases/submit-order',
    '/purchases/grns',
    '/purchases/grns/:id',
  ],

  'Purchase User': [
    '/dashboard',
    '/purchases',
    '/purchases/receipts',
    '/purchases/receipts/new',
    '/suppliers',
    '/inventory/stock-summary',
    '/products', // View products
  ],

  // Stock/Inventory roles
  'Stock Manager': [
    ...combineRoutes('DASHBOARD', 'INVENTORY', 'WAREHOUSES', 'PRODUCTS', 'REPORTS', 'STOCK_TRANSFERS'),
    '/purchases', // View purchases for stock management
    '/purchases/receipts',
    '/inventory/multi-level-reconciliation/new',
  ],

  'Stock User': [
    '/dashboard',
    ...combineRoutes('INVENTORY', 'WAREHOUSES', 'STOCK_TRANSFERS'),
    '/products', // View products
    '/inventory/stock-summary',
    '/inventory/low-stock',
    '/inventory/stock-ledger',
    '/inventory/item-details',
  ],

  // Quality Manager - Can perform quality checks and stock takes in multi-level reconciliation
  'Quality Manager': [
    '/dashboard',
    '/inventory/stock-summary', // View stock for quality checks
    '/products', // View products
    // Multi-level reconciliation access (can add stock take when status is "Pending Quality Manager")
    '/inventory/multi-level-reconciliation',
    '/inventory/multi-level-reconciliation/:id',
    '/inventory/multi-level-reconciliation/:id/stock-take',
  ],

  // Accounts roles
  'Accounts Manager': [
    ...combineRoutes('DASHBOARD', 'CUSTOMERS', 'SUPPLIERS', 'REPORTS', 'SETTINGS'),
    '/sales/history',
    '/purchases',
    '/purchases/receipts',
    '/settings/bank-accounts',
    '/settings/payment-methods',
    '/settings/account-provisioning',
    '/settings/loyalty-programs',
  ],

  'Accounts User': [
    '/dashboard',
    '/customers',
    '/customers/credit',
    '/suppliers',
    '/sales/history',
    '/purchases',
    '/purchases/receipts',
    ...combineRoutes('REPORTS'),
  ],

  // Item/Product roles
  'Item Manager': [
    ...combineRoutes('DASHBOARD', 'PRODUCTS', 'INVENTORY', 'REPORTS'),
    '/inventory/stock-summary',
    '/inventory/low-stock',
  ],

  // Desk User - POS operations
  'Desk User': [
    '/dashboard',
    '/sales',
    '/sales/history',
    '/products', // View products for POS
    '/customers', // View customers
    '/inventory/stock-summary', // Check stock
  ],

  // Teller - Only Sales module (POS + history + customers)
  Teller: [
    '/dashboard',
    ...combineRoutes('SALES', 'CUSTOMERS'),
    '/products', // Look up products for sales
    '/inventory/stock-summary', // Check stock availability
  ],

  // Waiter - POS access (create orders) + sales history
  Waiter: [
    '/dashboard',
    ...combineRoutes('SALES'),
    '/sales/history',
  ],

  // Guest - Limited read-only access
  Guest: [
    '/dashboard',
    '/products', // View only
    '/sales/history', // View only
  ],

  // Employee - Basic access
  Employee: [
    '/dashboard',
    '/sales',
    '/sales/history',
    '/products',
    '/customers',
    '/inventory/stock-summary',
  ],

  // Employee Self Service - Limited self-service access
  'Employee Self Service': [
    '/dashboard',
    '/sales/history', // View own sales
  ],

  // Delivery roles
  'Delivery Manager': [
    ...combineRoutes('DASHBOARD', 'SALES', 'CUSTOMERS', 'REPORTS'),
    '/sales/history',
    '/inventory/stock-summary',
  ],

  'Delivery User': [
    '/dashboard',
    '/sales/history',
    '/customers',
  ],

  // Agent roles
  'Agent Manager': [
    ...combineRoutes('DASHBOARD', 'SALES', 'CUSTOMERS', 'REPORTS'),
    '/products',
  ],

  Agent: [
    '/dashboard',
    '/sales',
    '/sales/history',
    '/customers',
    '/products',
  ],

  // Customer - Very limited access (if they have desk access)
  Customer: [
    '/dashboard',
    '/products', // View products
    '/sales/history', // View own orders
  ],

  // Analytics - Read-only access to reports and data
  Analytics: [
    '/dashboard',
    ...combineRoutes('REPORTS', 'SALES', 'PURCHASES', 'INVENTORY'),
    '/sales/history',
    '/purchases',
    '/inventory/stock-summary',
    '/products',
  ],

  // Auditor - Read-only access to financial and transaction data
  Auditor: [
    '/dashboard',
    '/sales/history',
    '/purchases',
    '/purchases/receipts',
    ...combineRoutes('CUSTOMERS', 'SUPPLIERS', 'REPORTS'),
    '/settings/bank-accounts',
  ],

  // Expense Approver - Access to financial approvals
  'Expense Approver': [
    '/dashboard',
    '/purchases',
    '/purchases/receipts',
    '/reports',
    '/settings/payment-methods',
  ],

  // Dashboard Manager - Access to dashboard and reports
  'Dashboard Manager': [
    '/dashboard',
    ...combineRoutes('REPORTS'),
    '/sales/history',
    '/purchases',
    '/inventory/stock-summary',
  ],

  // Agriculture roles (if applicable)
  'Agriculture Manager': [
    ...combineRoutes('DASHBOARD', 'PRODUCTS', 'INVENTORY', 'PURCHASES', 'REPORTS'),
  ],

  'Agriculture User': [
    '/dashboard',
    '/products',
    '/inventory/stock-summary',
    '/purchases',
  ],

  // Academics roles (if applicable)
  'Academics User': [
    '/dashboard',
    '/products',
    '/sales',
    '/sales/history',
  ],

  // Blogger - Limited content access
  Blogger: [
    '/dashboard',
    '/products', // View products for content
  ],
};

/**
 * Check if a route path matches any allowed route pattern
 * Supports exact matches and prefix matches for dynamic routes
 */
export const isRouteAllowed = (routePath, allowedRoutes) => {
  // Return false if routePath is not a valid string
  if (!routePath || typeof routePath !== 'string') {
    return false;
  }

  if (!allowedRoutes || allowedRoutes.length === 0) {
    return false;
  }

  // Normalize route path (remove trailing slash)
  const normalizedPath = routePath.replace(/\/$/, '');

  // Check for exact match
  if (allowedRoutes.includes(normalizedPath)) {
    return true;
  }

  // Check for prefix match (for dynamic routes like /sales/invoice/:id)
  return allowedRoutes.some((allowedRoute) => {
    // Ensure allowedRoute is also a valid string
    if (!allowedRoute || typeof allowedRoute !== 'string') {
      return false;
    }

    // Normalize allowed route
    const normalizedAllowed = allowedRoute.replace(/\/$/, '');

    // Exact match (already checked above, but keep for consistency)
    if (normalizedPath === normalizedAllowed) {
      return true;
    }

    // Handle dynamic routes - if allowed route contains :, check base path
    // e.g., '/sales/invoice/:id' matches '/sales/invoice/123'
    // or '/inventory/multi-level-reconciliation/:id' matches '/inventory/multi-level-reconciliation/123/stock-take'
    if (normalizedAllowed.includes(':')) {
      // Split on ':' to get the base path before the first parameter
      const basePath = normalizedAllowed.split(':')[0];
      // Remove trailing slash from base path for comparison
      const normalizedBase = basePath.replace(/\/$/, '');
      
      // Check if the route path starts with the base path
      // This handles cases like:
      // - '/inventory/multi-level-reconciliation/:id' matching '/inventory/multi-level-reconciliation/123'
      // - '/inventory/multi-level-reconciliation/:id/stock-take' matching '/inventory/multi-level-reconciliation/123/stock-take'
      if (normalizedPath.startsWith(normalizedBase + '/') || normalizedPath === normalizedBase) {
        return true;
      }
    }

    return false;
  });
};

/**
 * Get all allowed routes for a set of roles
 */
export const getAllowedRoutes = (roles) => {
  if (!roles || roles.length === 0) {
    return [];
  }

  // Filter out "All" role as it should not grant automatic access
  // Users with "All" role will only get access based on their other roles
  const effectiveRoles = roles.filter(role => role !== 'All');

  if (effectiveRoles.length === 0) {
    return [];
  }

  // Combine all routes from all user roles
  const allowedRoutesSet = new Set();
  effectiveRoles.forEach((role) => {
    const roleRoutes = ROLE_ACCESS_CONFIG[role] || [];
    roleRoutes.forEach((route) => allowedRoutesSet.add(route));
  });

  // Pattern-based dynamic grants:
  // - any role containing 'purchase' -> grant PURCHASES and STOCK_TRANSFERS groups
  // - any role containing 'stock' -> grant INVENTORY group
  // - any role containing 'quality' -> grant full access (merge all configured routes)
  const lowerRolesCombined = effectiveRoles.join(' ').toLowerCase();

  if (lowerRolesCombined.includes('purchase')) {
    combineRoutes('PURCHASES', 'STOCK_TRANSFERS').forEach((route) => allowedRoutesSet.add(route));
  }

  if (lowerRolesCombined.includes('stock')) {
    combineRoutes('INVENTORY').forEach((route) => allowedRoutesSet.add(route));
  }

  if (lowerRolesCombined.includes('quality')) {
    // Grant everything configured (best-effort full access)
    // Add all route groups
    Object.values(ROUTE_GROUPS).flat().forEach((route) => allowedRoutesSet.add(route));
    // Also add any individually configured role routes
    Object.values(ROLE_ACCESS_CONFIG).flat().forEach((route) => allowedRoutesSet.add(route));
  }

  return Array.from(allowedRoutesSet);
};

/**
 * Check if user with given roles can access a specific route
 */
export const canAccessRoute = (routePath, roles) => {
  if (!roles || roles.length === 0) {
    return false;
  }

  // Full-access roles bypass route checks entirely
  // Note: "All" role is treated as a normal role and only grants access to explicitly configured routes
  if (roles.some(role => ['Administrator', 'System Manager', 'POS Administrator'].includes(role))) {
    return true;
  }

  const allowedRoutes = getAllowedRoutes(roles);
  return isRouteAllowed(routePath, allowedRoutes);
};

export const getModuleKeyFromRoute = (_routePath) => {
  return null;
};

